import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  IconButton,
  Alert,
  Stack,
  Link,
  Box,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useToastContext as useToast } from "../../../context/ToastContext";
import { useLogin, useRegister, useResendVerification } from '../../../hooks/useAuth';
import type { User } from "../../../models/User";
import { usePostHog } from "@posthog/react";

interface AuthProps {
  isOpen: boolean;
  onSetUser: (user: User) => void;
  onClose?: () => void;
}

function normalizeAuthInput({
    username,
    email,
  }: {
    username: string;
    email?: string;
  }) {
    return {
      username: username.trim(),
      email: email?.trim().toLowerCase(),
    };
  }

export default function Auth({ onSetUser, onClose, isOpen: open }: AuthProps) {
  const { addToast } = useToast();
  const navigate = useNavigate();
  const createUserMutation = useRegister();
  const loginUserMutation = useLogin();
  const resendVerificationMutation = useResendVerification();
  const posthog = usePostHog();

  // Form state
  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [mode, setMode] = useState<"login" | "register">("login");
  const [formError, setFormError] = useState("");
  const [needsEmailVerification, setNeedsEmailVerification] = useState(false);
  const [unverifiedEmail, setUnverifiedEmail] = useState("");

  const handleClose = () => { onClose?.(); };

  const handleRegister = async (
    normalizedUsername: string,
    normalizedEmail: string
  ) => {
    try {
      // Transform to match RegisterData interface
      const registerData = {
        username: normalizedUsername,
        email: normalizedEmail,
        password: password, // Note: 'password' not 'passwordHash'
        team: null,
        avatarUrl: "/default-avatar.png",
      };

      await createUserMutation.mutateAsync(registerData);

      posthog?.capture('user_registered', { username: normalizedUsername });

      // Don't log them in - they need to verify email first
      addToast("Account created! Please check your email to verify your account.", { type: "success" });
      
      // Switch to login mode and show verification message
      setMode("login");
      setNeedsEmailVerification(true);
      setUnverifiedEmail(normalizedEmail);
      setFormError("Please verify your email before logging in. Check your inbox for the verification link.");
    } catch (err: any) {
      console.error(err);
      
      // Handle specific error messages from backend
      const errorMessage = err.response?.data?.message || "Failed to create account.";
      setFormError(errorMessage);
      addToast(errorMessage, { type: "error" });
    }
  };

  const handleLogin = async (normalizedUsername: string) => {
    try {
      const loggedInUser = await loginUserMutation.mutateAsync({
        username: normalizedUsername,
        password: password,
      });

      localStorage.setItem("triviaUser", JSON.stringify(loggedInUser));
      onSetUser(loggedInUser);

      posthog?.capture('user_logged_in', { username: loggedInUser.username });
      posthog?.identify(loggedInUser.id, { username: loggedInUser.username });

      addToast(`Welcome back, ${loggedInUser.username}!`, { type: "success" });

      setTimeout(handleClose, 100);
    } catch (err: any) {
      console.error(err);
      
      const errorMessage = err.response?.data?.message || '';
      
      // Check if error is about unverified email
      if (errorMessage.includes('verify your email')) {
        setNeedsEmailVerification(true);
        setUnverifiedEmail(err.response?.data?.email || ''); // Backend returns user's email when login fails due to unverified email (used for resend button)
        setFormError(errorMessage);
        addToast(errorMessage, { type: "error" });
      } else if (err.response?.status === 401) {
        setFormError("Invalid username or password");
        addToast("Invalid username or password", { type: "error" });
      } else {
        setFormError("Login failed. Please try again.");
        addToast("Login failed", { type: "error" });
      }
    }
  };

  const handleResendVerification = async () => {
    try {
      const emailToUse = unverifiedEmail || email;
      if (!emailToUse) {
        addToast('Please enter your email address', { type: 'error' });
        return;
      }
      
      await resendVerificationMutation.mutateAsync(emailToUse);
      addToast('Verification email sent! Check your inbox.', { type: 'success' });
    } catch (error) {
      console.error(error);
      addToast('Failed to resend verification email', { type: 'error' });
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setFormError("");
    setNeedsEmailVerification(false);

    const { username: normalizedUsername, email: normalizedEmail } =
      normalizeAuthInput({ username, email });

    if (!normalizedUsername || !password) {
      setFormError("Username and password are required.");
      return;
    }

    if (mode === "register" && !normalizedEmail) {
      setFormError("Email is required.");
      return;
    }

    if (mode === "register") {
      handleRegister(normalizedUsername, normalizedEmail!);
    } else {
      handleLogin(normalizedUsername);
    }
  };

  const switchMode = () => {
    setMode(mode === "login" ? "register" : "login");
    setFormError("");
    setNeedsEmailVerification(false);
    setEmail("");
    setPassword("");
  };

  const handleForgotPassword = () => {
    handleClose();
    navigate("/forgot-password");
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth disableScrollLock>
      <IconButton
        sx={{ position: "absolute", top: 8, right: 8 }}
        size="small"
        onClick={handleClose}
      >
        <CloseIcon />
      </IconButton>

      <DialogTitle>{mode === "login" ? "Login" : "Register"}</DialogTitle>

      <DialogContent>
        <form onSubmit={handleSubmit}>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleSubmit(e as any)}
              fullWidth
              autoFocus
            />

            {mode === "register" && (
              <TextField
                label="Email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleSubmit(e as any)}
                fullWidth
              />
            )}

            <TextField
              label="Password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleSubmit(e as any)}
              fullWidth
            />

            {/* Resend verification button - only show when email needs verification */}
            {needsEmailVerification && mode === "login" && (
              <Button
                variant="outlined"
                fullWidth
                onClick={handleResendVerification}
                disabled={resendVerificationMutation.isPending}
              >
                {resendVerificationMutation.isPending
                  ? 'Sending...'
                  : 'Resend Verification Email'}
              </Button>
            )}

            {/* Forgot Password link - only show in login mode when not showing verification */}
            {mode === "login" && !needsEmailVerification && (
              <Box display="flex" justifyContent="flex-end">
                <Link
                  component="button"
                  type="button"
                  variant="body2"
                  onClick={handleForgotPassword}
                  sx={{ cursor: "pointer" }}
                >
                  Forgot password?
                </Link>
              </Box>
            )}

            {formError && <Alert severity="error">{formError}</Alert>}
          </Stack>
        </form>
      </DialogContent>

      <DialogActions>
        <Stack
          direction="row"
          justifyContent="space-between"
          width="100%"
          spacing={2}
        >
          <Button onClick={switchMode}>
            {mode === "login" ? "Create an account" : "Already have an account?"}
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={createUserMutation.isPending || loginUserMutation.isPending}
          >
            {mode === "login" ? "Login" : "Register"}
          </Button>
        </Stack>
      </DialogActions>
    </Dialog>
  );
}